/**
 * Admin permission hook. One module-level cache shared across components.
 *
 * Permissions are item → level ('read' | 'write'). `super_admin` returns
 * `isSuperAdmin: true` and `has(...)` always succeeds at any level.
 *
 * Call `reload()` after any permission grant change so the UI re-gates.
 */
import { useEffect, useState, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

export type PermissionLevel = 'read' | 'write';

interface PermissionState {
  isSuperAdmin: boolean;
  levels: Readonly<Record<string, PermissionLevel>>;
  loaded: boolean;
}

const INITIAL: PermissionState = { isSuperAdmin: false, levels: {}, loaded: false };

let cache: PermissionState = INITIAL;
let inflight: Promise<PermissionState> | null = null;
const listeners = new Set<(s: PermissionState) => void>();

function notify() {
  for (const l of listeners) l(cache);
}

async function fetchPermissions(): Promise<PermissionState> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await api<{ isSuperAdmin: boolean; levels: Record<string, PermissionLevel> }>('/api/admin/me/permissions');
      cache = { isSuperAdmin: !!res.isSuperAdmin, levels: res.levels || {}, loaded: true };
    } catch {
      cache = { isSuperAdmin: false, levels: {}, loaded: true };
    }
    notify();
    return cache;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/** Force-clear cache (e.g. on logout). */
export function clearPermissionCache() {
  cache = INITIAL;
  inflight = null;
  notify();
}

function satisfies(granted: PermissionLevel | undefined, required: PermissionLevel): boolean {
  if (!granted) return false;
  if (required === 'read') return granted === 'read' || granted === 'write';
  return granted === 'write';
}

export interface AdminPermissions {
  isSuperAdmin: boolean;
  levels: Readonly<Record<string, PermissionLevel>>;
  loaded: boolean;
  /** Does the admin satisfy `key` at the given level (default 'read')? */
  has: (key: string, level?: PermissionLevel) => boolean;
  /** Does the admin satisfy at least one of these keys at `level`? */
  hasAny: (keys: readonly string[], level?: PermissionLevel) => boolean;
  /** Granted level for a single key, or undefined. */
  levelOf: (key: string) => PermissionLevel | undefined;
  reload: () => Promise<void>;
}

export function useAdminPermissions(): AdminPermissions {
  const { role } = useAuth();
  const [state, setState] = useState<PermissionState>(cache);

  useEffect(() => {
    if (role !== 'admin') {
      setState(INITIAL);
      return;
    }
    listeners.add(setState);
    if (!cache.loaded) void fetchPermissions();
    else setState(cache);
    return () => { listeners.delete(setState); };
  }, [role]);

  const has = useCallback(
    (key: string, level: PermissionLevel = 'read') => state.isSuperAdmin || satisfies(state.levels[key], level),
    [state],
  );
  const hasAny = useCallback(
    (keys: readonly string[], level: PermissionLevel = 'read') => state.isSuperAdmin || keys.some(k => satisfies(state.levels[k], level)),
    [state],
  );
  const levelOf = useCallback(
    (key: string) => state.isSuperAdmin ? 'write' as PermissionLevel : state.levels[key],
    [state],
  );
  const reload = useCallback(async () => { await fetchPermissions(); }, []);

  return { isSuperAdmin: state.isSuperAdmin, levels: state.levels, loaded: state.loaded, has, hasAny, levelOf, reload };
}
